import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../api'
import { useAdminFixturesFromQuery } from '../fixtures'
import { PageHeader, StateBlock } from '../../shared/StateBlock'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { formatWhen, safeAdminError } from '../safe-ui'
import { money } from '../../client/prediction-filter'

type WithdrawalRow = {
  id: number
  reference: string
  clientId?: number
  amount: string
  currency?: string
  status: string
  walletAddress?: string
  payoutTxid?: string | null
  rejectionReason?: string | null
  submittedAt?: string
  mt5Debited?: boolean
  mt5Refunded?: boolean
  mt5LoginMasked?: string
}

export function AdminWithdrawalsPage() {
  const fixtures = useAdminFixturesFromQuery()
  const q = fixtures ? '?fixtures=1' : ''
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [rows, setRows] = useState<WithdrawalRow[]>([])
  const [statusFilter, setStatusFilter] = useState('PENDING_ADMIN_REVIEW')
  const [selected, setSelected] = useState<WithdrawalRow | null>(null)
  const [timeline, setTimeline] = useState<Array<Record<string, unknown>>>([])
  const [busy, setBusy] = useState(false)
  const [dialog, setDialog] = useState<'approve' | 'reject' | 'processing' | 'complete' | null>(null)
  const [txid, setTxid] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      if (fixtures) {
        setRows([])
        return
      }
      const data = await adminApi.listWithdrawals({
        status: statusFilter || undefined,
        limit: 50,
      })
      setRows((data.withdrawals || []) as WithdrawalRow[])
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load withdrawals.'))
    } finally {
      setLoading(false)
    }
  }, [fixtures, statusFilter])

  useEffect(() => {
    void load()
  }, [load])

  async function openDetail(row: WithdrawalRow) {
    setSelected(row)
    setTimeline([])
    setTxid(String(row.payoutTxid || ''))
    setError('')
    setNotice('')
    if (fixtures) return
    try {
      const data = await adminApi.getWithdrawal(row.id)
      setSelected(data.withdrawal as WithdrawalRow)
      setTimeline(data.timeline || [])
      setTxid(String((data.withdrawal as WithdrawalRow).payoutTxid || ''))
    } catch (err) {
      setError(safeAdminError(err, 'Unable to load withdrawal.'))
    }
  }

  async function onApprove() {
    if (!selected) return
    setBusy(true)
    setError('')
    try {
      await adminApi.approveWithdrawal(selected.id, txid.trim() || undefined)
      setNotice(`Withdrawal ${selected.reference} approved.`)
      setDialog(null)
      await load()
      await openDetail(selected)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to approve withdrawal.'))
    } finally {
      setBusy(false)
    }
  }

  async function onProcessing() {
    if (!selected || !txid.trim()) return
    setBusy(true)
    setError('')
    try {
      await adminApi.markWithdrawalProcessing(selected.id, txid.trim())
      setNotice(`Withdrawal ${selected.reference} marked processing.`)
      setDialog(null)
      await load()
      await openDetail(selected)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to mark processing.'))
    } finally {
      setBusy(false)
    }
  }

  async function onComplete() {
    if (!selected) return
    setBusy(true)
    setError('')
    try {
      await adminApi.completeWithdrawal(selected.id, txid.trim() || undefined)
      setNotice(`Withdrawal ${selected.reference} completed.`)
      setDialog(null)
      await load()
      await openDetail(selected)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to complete withdrawal.'))
    } finally {
      setBusy(false)
    }
  }

  async function onReject(reason: string) {
    if (!selected) return
    setBusy(true)
    setError('')
    try {
      await adminApi.rejectWithdrawal(selected.id, reason)
      setNotice(`Withdrawal ${selected.reference} rejected and refunded.`)
      setDialog(null)
      await load()
      await openDetail(selected)
    } catch (err) {
      setError(safeAdminError(err, 'Unable to reject withdrawal.'))
    } finally {
      setBusy(false)
    }
  }

  const status = String(selected?.status || '')

  return (
    <section className="cp-page">
      <PageHeader eyebrow="Withdrawals" title="Withdrawal operations">
        <p className="cp-muted">
          Review debited Demo withdrawals. Approve with payout proof, or reject to refund MT5.
        </p>
      </PageHeader>

      {error ? <StateBlock tone="warn" title="Error">{error}</StateBlock> : null}
      {notice ? <p className="cp-ok">{notice}</p> : null}

      <div className={`cp-panel${loading ? ' is-refreshing' : ''}`}>
        <div className="cp-platform-actions" style={{ marginBottom: '1rem', gap: '0.75rem' }}>
          <label className="cp-field" style={{ minWidth: '14rem' }}>
            Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="PENDING_ADMIN_REVIEW">Pending review</option>
              <option value="APPROVED">Approved</option>
              <option value="PROCESSING">Processing</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED_AND_REFUNDED">Rejected & refunded</option>
              <option value="RECONCILIATION_REQUIRED">Reconciliation</option>
            </select>
          </label>
          <Button type="button" variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </div>

        <h2>Withdrawal queue</h2>
        {loading ? (
          <p className="cp-muted ap-inline-status" aria-live="polite">
            Loading…
          </p>
        ) : null}
        <ResponsiveTable
          rows={rows}
          rowKey={(r) => String(r.id)}
          empty={loading ? 'Loading…' : 'No withdrawal records for this filter.'}
          columns={[
            { key: 'ref', header: 'Reference', render: (r) => r.reference },
            { key: 'client', header: 'Client', render: (r) => (r.clientId != null ? `#${r.clientId}` : '—') },
            {
              key: 'amount',
              header: 'Amount',
              render: (r) => `${money(r.amount)} ${r.currency || 'USD'}`,
            },
            { key: 'status', header: 'Status', render: (r) => r.status },
            { key: 'created', header: 'Submitted', render: (r) => formatWhen(r.submittedAt) },
            {
              key: 'actions',
              header: '',
              render: (r) => (
                <Button type="button" variant="ghost" size="sm" onClick={() => void openDetail(r)}>
                  Open
                </Button>
              ),
            },
          ]}
        />
      </div>

      {selected ? (
        <div className="cp-panel" style={{ marginTop: '1.25rem' }}>
          <h2>{selected.reference}</h2>
          <dl className="cp-wd__detail-grid">
            <div>
              <dt>Status</dt>
              <dd>{selected.status}</dd>
            </div>
            <div>
              <dt>Amount</dt>
              <dd>
                {money(selected.amount)} {selected.currency || 'USD'}
              </dd>
            </div>
            <div>
              <dt>MT5</dt>
              <dd>{selected.mt5LoginMasked || '—'}</dd>
            </div>
            <div>
              <dt>Wallet</dt>
              <dd className="cp-wd__mono">{selected.walletAddress || '—'}</dd>
            </div>
            <div>
              <dt>Debited / Refunded</dt>
              <dd>
                {selected.mt5Debited ? 'Yes' : 'No'} / {selected.mt5Refunded ? 'Yes' : 'No'}
              </dd>
            </div>
            {selected.rejectionReason ? (
              <div>
                <dt>Rejection reason</dt>
                <dd>{selected.rejectionReason}</dd>
              </div>
            ) : null}
          </dl>

          <label className="cp-field" style={{ marginTop: '1rem' }}>
            Payout TXID / proof
            <input
              type="text"
              value={txid}
              onChange={(e) => setTxid(e.target.value)}
              placeholder="Required to mark processing/complete"
              autoComplete="off"
              spellCheck={false}
            />
          </label>

          <div className="cp-platform-actions" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
            {status === 'PENDING_ADMIN_REVIEW' ? (
              <>
                <Button type="button" variant="primary" disabled={busy} onClick={() => setDialog('approve')}>
                  Approve
                </Button>
                <Button type="button" variant="secondary" disabled={busy} onClick={() => setDialog('reject')}>
                  Reject & refund
                </Button>
              </>
            ) : null}
            {status === 'APPROVED' ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  disabled={busy || !txid.trim()}
                  onClick={() => setDialog('processing')}
                >
                  Mark processing
                </Button>
                <Button type="button" variant="secondary" disabled={busy} onClick={() => setDialog('reject')}>
                  Reject & refund
                </Button>
              </>
            ) : null}
            {status === 'PROCESSING' || status === 'APPROVED' ? (
              <Button
                type="button"
                variant="primary"
                disabled={busy || !(txid.trim() || selected.payoutTxid)}
                onClick={() => setDialog('complete')}
              >
                Complete
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>

          <h3 style={{ marginTop: '1.25rem' }}>Timeline</h3>
          <ol className="cp-wd__timeline">
            {timeline.length === 0 ? <li className="cp-muted">No events.</li> : null}
            {timeline.map((ev, idx) => (
              <li key={`${String(ev.timestamp)}-${idx}`}>
                <strong>{String(ev.event_type || 'Update')}</strong>
                <span>{formatWhen(String(ev.timestamp || ''))}</span>
                {ev.description ? <p>{String(ev.description)}</p> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="cp-platform-actions" style={{ marginTop: '1rem' }}>
        <Link className="pp-btn pp-btn--secondary" to={`/admin${q}`}>
          Back to dashboard
        </Link>
      </div>

      <ConfirmDialog
        open={dialog === 'approve'}
        title="Approve withdrawal"
        body="Approve this debited withdrawal for manual USDT payout. No automatic blockchain transfer."
        confirmLabel="Approve"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => void onApprove()}
      />
      <ConfirmDialog
        open={dialog === 'processing'}
        title="Mark processing"
        body={`Record payout as processing with TXID ${txid.trim() || '—'}.`}
        confirmLabel="Mark processing"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => void onProcessing()}
      />
      <ConfirmDialog
        open={dialog === 'complete'}
        title="Complete withdrawal"
        body="Mark payout as completed only after real payout confirmation. Never fake success."
        confirmLabel="Complete"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => void onComplete()}
      />
      <ConfirmDialog
        open={dialog === 'reject'}
        title="Reject & refund"
        body="Reject this request and refund the exact debited amount to Demo MT5."
        confirmLabel="Reject & refund"
        danger
        requireReason
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => void onReject(reason)}
      />
    </section>
  )
}
